import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? {...n, read: true} : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({...n, read: true})));
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.type === 'new_user_registration') {
      navigate(`/user/${notification.from_user_id}`);
    } else if (notification.type === 'new_feedback') {
      navigate('/admin'); // Navigate to admin dashboard to see feedback
    } else if (notification.cigar_id) {
      if (notification.type === 'new_comment' || notification.type === 'comment_reply') {
        navigate(`/cigar/${notification.cigar_id}/discussions`);
      } else {
        navigate(`/cigar/${notification.cigar_id}`);
      }
    }
  };

  const getNotificationMessage = (notification) => {
    // If message is already set, use it directly (new format)
    if (notification.message && !notification.message.includes(notification.from_username)) {
      return notification.message;
    }
    
    switch (notification.type) {
      case 'new_user_registration':
        return ' just created an account';
      case 'new_feedback':
        return ' submitted feedback';
      case 'new_favorite':
        return ' favorited a cigar';
      case 'new_rating':
        return ' rated a cigar';
      case 'new_comment':
        return ' commented on a cigar';
      case 'comment_reply':
      default:
        return ' replied to your comment';
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] pb-24 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="mb-6 flex justify-center">
            <div className="w-32 h-32 rounded-full bg-white/5 flex items-center justify-center border-2 border-white/10">
              <Bell size={64} className="text-white/30" />
            </div>
          </div>
          <h2 className="playfair text-2xl text-white mb-6">Sign in to view notifications</h2>
          <Button
            data-testid="notifications-signin-button"
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-br from-amber-600 to-amber-800 text-white px-12 py-6 rounded-full text-lg"
          >
            Sign In
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white">Notifications</h1>
        {unreadCount > 0 ? (
          <button 
            onClick={markAllAsRead}
            className="text-amber-500 text-sm hover:text-amber-400"
          >
            Mark all read
          </button>
        ) : (
          <div className="w-20"></div>
        )}
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto pb-24">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center text-white/50 py-8">No notifications yet</div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`flex items-start gap-3 p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${!notification.read ? 'bg-amber-500/5' : ''}`}
            >
              {/* Profile Picture */}
              {notification.from_profile_pic ? (
                <img 
                  src={notification.from_profile_pic} 
                  alt={notification.from_username}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {notification.from_username?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-white">
                  {notification.message || (
                    <>
                      <span className="font-semibold">{notification.from_username}</span>
                      {getNotificationMessage(notification)}
                    </>
                  )}
                </p>
                <p className="text-white/40 text-sm mt-1">{formatDateTime(notification.created_at)}</p>
              </div>
              
              {/* Unread indicator */}
              {!notification.read && (
                <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-2"></div>
              )}
            </div>
          ))
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default NotificationsPage;
